import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { BoltIcon } from '@heroicons/react/24/solid';
import { CheckIcon } from '@heroicons/react/24/outline';
import { functionsEurope } from '../firebase';

const PACKS = [
  {
    pack: '1',
    name: '1 publish',
    price: '£39',
    sub: 'one-off',
    perGame: '£39 per game',
    blurb: 'Ship a single campaign live.',
    features: [
      'Shareable link + QR code',
      'Leaderboard & lead capture',
      'Full analytics + CTA tracking',
      'Free edits forever',
    ],
  },
  {
    pack: '5',
    name: '5-publish pack',
    price: '£149',
    sub: 'save £46',
    perGame: '~£30 per game',
    blurb: 'For repeat campaigns and agencies.',
    features: [
      'Everything in the single pack',
      '~£30 per published game',
      'Credits never expire',
      'Mix across any game type',
    ],
    highlight: true,
  },
];

/**
 * Credit-pack picker. Rendered inside ModalBasic by CreditModalContext, or
 * embedded directly in Settings → Plan & billing. Picking a pack opens Stripe
 * Checkout; on return the webhook grants the credit server-side and the
 * dashboard / campaign view shows a confirmation. No auto-publish.
 */
export default function CreditPackModal({ campaignId, onClose }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  const handleBuy = async (pack) => {
    setBusy(pack);
    setError(null);
    try {
      const createCreditPackCheckout = httpsCallable(functionsEurope, 'createCreditPackCheckout');
      const { data } = await createCreditPackCheckout({
        pack,
        campaignId,
        origin: typeof window !== 'undefined' ? window.location.origin : undefined,
      });
      if (!data?.url) throw new Error('Stripe did not return a checkout URL.');
      window.location.assign(data.url);
    } catch (err) {
      console.error('createCreditPackCheckout failed', err);
      setError(err?.message || 'Could not start checkout. Please try again.');
      setBusy(null);
    }
  };

  return (
    <div className="p-5 sm:p-6" style={{ fontFamily: '"Inter", sans-serif' }}>
      <p className="text-sm gch-text-secondary mb-5">
        Pay only when you ship. 1 credit = 1 published game. Credits never expire, edits are free forever.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        {PACKS.map((p) => (
          <button
            key={p.pack}
            type="button"
            onClick={() => handleBuy(p.pack)}
            disabled={busy !== null}
            className={`relative text-left rounded-2xl p-5 border-2 transition-all duration-200 ${
              p.highlight
                ? 'border-amber-400/60 bg-gradient-to-br from-amber-500/10 to-amber-500/[0.03] hover:border-amber-400/80 hover:from-amber-500/15'
                : 'border-[var(--admin-border)] bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.04]'
            } ${busy === p.pack ? 'opacity-60' : ''} disabled:cursor-wait`}
          >
            {p.highlight && (
              <span className="absolute -top-2 right-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400 text-[10px] font-bold uppercase tracking-wider text-slate-900">
                <BoltIcon className="w-3 h-3" />
                Best value
              </span>
            )}

            <h3 className="text-sm font-semibold gch-text-secondary uppercase tracking-wider">
              {p.name}
            </h3>

            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-4xl font-bold gch-text-primary tracking-tight">{p.price}</span>
              <span className={`text-xs font-semibold ${p.highlight ? 'text-amber-300' : 'gch-text-muted'}`}>
                {p.sub}
              </span>
            </div>
            <p className="mt-1 text-xs gch-text-muted">{p.perGame}</p>

            <ul className="mt-4 space-y-1.5">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-xs gch-text-secondary">
                  <CheckIcon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${p.highlight ? 'text-amber-400' : 'text-emerald-400'}`} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <div
              className={`mt-5 w-full inline-flex justify-center items-center px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                p.highlight
                  ? 'bg-amber-400 text-slate-900 hover:bg-amber-300'
                  : 'bg-white/10 text-white hover:bg-white/15'
              }`}
            >
              {busy === p.pack ? 'Opening checkout…' : p.highlight ? 'Buy 5-pack' : 'Buy 1 credit'}
            </div>
          </button>
        ))}
      </div>

      {error && (
        <p className="mt-4 text-xs text-red-400" role="alert">{error}</p>
      )}

      <div className="mt-5 flex items-center justify-between gap-4">
        <p className="text-[11px] gch-text-muted">
          Secure checkout via Stripe. Refunds at support@smilesflow.com.
        </p>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            disabled={busy !== null}
            className="text-xs gch-text-muted hover:gch-text-primary shrink-0"
          >
            Maybe later
          </button>
        )}
      </div>
    </div>
  );
}
